import { Box, Container, Skeleton, Stack } from '@mui/material';

export interface PageSkeletonProps {
  /**
   * Type of page skeleton to render
   */
  variant?: 'dashboard' | 'list' | 'form' | 'detail' | 'default';

  /**
   * Custom title for accessibility
   */
  title?: string;
}

/**
 * Dashboard skeleton with cards and metrics
 */
const DashboardSkeleton = () => (
  <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
    {/* Header */}
    <Box sx={{ mb: 4 }}>
      <Skeleton variant="text" width={280} height={48} />
      <Skeleton variant="text" width={160} height={24} sx={{ mt: 1 }} />
    </Box>

    {/* Safe to Spend Card */}
    <Skeleton
      variant="rounded"
      height={180}
      sx={{ mb: 3, borderRadius: 2 }}
    />

    {/* Spending Breakdown */}
    <Skeleton
      variant="rounded"
      height={200}
      sx={{ mb: 3, borderRadius: 2 }}
    />

    {/* Quick Purchase Check */}
    <Skeleton
      variant="rounded"
      height={140}
      sx={{ mb: 3, borderRadius: 2 }}
    />

    {/* Two-column layout */}
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    </Box>

    {/* Cash Flow Forecast */}
    <Skeleton
      variant="rounded"
      height={250}
      sx={{ borderRadius: 2 }}
    />
  </Container>
);

/**
 * List page skeleton (for transactions, etc.)
 */
const ListSkeleton = () => (
  <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
    {/* Header */}
    <Box sx={{ mb: 3 }}>
      <Skeleton variant="text" width={200} height={40} />
      <Skeleton variant="text" width={300} height={24} sx={{ mt: 1 }} />
    </Box>

    {/* Filter bar */}
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Skeleton variant="rounded" width={200} height={44} />
      <Skeleton variant="rounded" width={200} height={44} />
      <Skeleton variant="rounded" width={120} height={44} />
    </Box>

    {/* List items */}
    <Stack spacing={2}>
      {[...Array(6)].map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={80}
          sx={{ borderRadius: 2 }}
        />
      ))}
    </Stack>
  </Container>
);

/**
 * Form page skeleton (for settings, onboarding, etc.)
 */
const FormSkeleton = () => (
  <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
    {/* Header */}
    <Box sx={{ mb: 4 }}>
      <Skeleton variant="text" width={200} height={40} />
      <Skeleton variant="text" width={400} height={24} sx={{ mt: 1 }} />
    </Box>

    {/* Form fields */}
    <Stack spacing={3}>
      {[...Array(4)].map((_, i) => (
        <Box key={i}>
          <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Stack>

    {/* Submit button */}
    <Box sx={{ mt: 4 }}>
      <Skeleton variant="rounded" width={160} height={44} sx={{ borderRadius: 1 }} />
    </Box>
  </Container>
);

/**
 * Detail page skeleton (for individual item views)
 */
const DetailSkeleton = () => (
  <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
    {/* Header with back button */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Skeleton variant="text" width={200} height={40} />
    </Box>

    {/* Main card */}
    <Skeleton
      variant="rounded"
      height={300}
      sx={{ mb: 3, borderRadius: 2 }}
    />

    {/* Details section */}
    <Stack spacing={2}>
      {[...Array(5)].map((_, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={180} height={24} />
        </Box>
      ))}
    </Stack>
  </Container>
);

/**
 * Default simple skeleton
 */
const DefaultSkeleton = () => (
  <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
    <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
  </Container>
);

/**
 * PageSkeleton - Loading placeholder for page content
 * Prevents Cumulative Layout Shift (CLS) by reserving space
 */
export const PageSkeleton = ({ variant = 'default', title }: PageSkeletonProps) => {
  const skeletonContent = () => {
    switch (variant) {
      case 'dashboard':
        return <DashboardSkeleton />;
      case 'list':
        return <ListSkeleton />;
      case 'form':
        return <FormSkeleton />;
      case 'detail':
        return <DetailSkeleton />;
      default:
        return <DefaultSkeleton />;
    }
  };

  return (
    <div
      role="status"
      aria-label={title || `Loading ${variant} page`}
      aria-busy="true"
    >
      {skeletonContent()}
      <span className="sr-only">Loading content, please wait...</span>
    </div>
  );
};

export default PageSkeleton;
